import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { technologiesPageData } from "@/mocks/technologiesPageData";
import { techStackData } from "@/mocks/homeData";

function TechItem({
  name,
  logo,
  icon,
  featured,
  href,
  delay,
}: {
  name: string;
  logo?: string;
  icon?: string;
  featured?: boolean;
  href?: string;
  delay: number;
}) {
  const inner = (
    <div
      className={`group/tech flex flex-col items-center gap-2.5 p-2.5 md:p-3 rounded-xl border transition-all duration-300 cursor-pointer h-full justify-center ${
        featured
          ? "border-accent-300/40 bg-accent-50/50 hover:bg-accent-50/80 hover:border-accent-400/50 hover:shadow-md hover:shadow-accent-500/10"
          : "border-background-200/60 bg-background-50 hover:border-accent-300/40 hover:bg-accent-50/40 hover:shadow-sm hover:-translate-y-0.5"
      }`}
    >
      {logo ? (
        <img
          src={logo}
          alt={`${name} logo`}
          className={`object-contain transition-transform duration-300 group-hover/tech:scale-110 ${
            featured ? "h-12 sm:h-[52px]" : "h-11 sm:h-12"
          }`}
          style={{ width: "auto", maxWidth: "100px" }}
          loading="lazy"
        />
      ) : (
        <span className={`w-11 h-11 flex items-center justify-center ${featured ? "text-accent-600" : "text-accent-500"}`}>
          <i className={`${icon} text-2xl`} />
        </span>
      )}
      <span
        className={`text-[12px] md:text-[13px] font-semibold text-center leading-tight transition-colors duration-300 ${
          featured ? "text-accent-700" : "text-foreground-600 group-hover/tech:text-accent-600"
        }`}
      >
        {name}
      </span>
    </div>
  );

  if (href) {
    return (
      <a href={href} className="animate-fade-in-up h-full" style={{ animationDelay: `${delay}ms`, animationFillMode: "both" }}>
        {inner}
      </a>
    );
  }
  return (
    <div className="animate-fade-in-up h-full" style={{ animationDelay: `${delay}ms`, animationFillMode: "both" }}>
      {inner}
    </div>
  );
}

function CategoryBox({
  group,
  index,
}: {
  group: (typeof technologiesPageData.categories)[0];
  index: number;
}) {
  return (
    <AnimatedSection delay={index * 80} className="h-full">
      <div
        className={`group relative h-full flex flex-col rounded-2xl border bg-background-100 p-5 md:p-6 transition-all duration-400 hover:-translate-y-1 ${
          group.isAi
            ? "border-accent-300/40 shadow-sm hover:shadow-md hover:shadow-accent-500/10"
            : "border-background-200/70 hover:border-accent-300/30 hover:shadow-md hover:shadow-accent-500/5"
        }`}
      >
        {group.isAi && (
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-accent-500 via-secondary-500 to-primary-500 rounded-t-2xl" />
        )}

        <div className="flex items-center gap-3 mb-2">
          <div
            className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 transition-colors duration-300 ${
              group.isAi
                ? "bg-accent-500/10 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50"
                : "bg-primary-50 text-primary-600 group-hover:bg-accent-500 group-hover:text-background-50"
            }`}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`${group.icon} text-lg`} />
            </span>
          </div>
          <h3 className="text-sm md:text-base font-bold text-foreground-950 font-heading">
            {group.title}
          </h3>
        </div>

        <p className="text-xs md:text-[13px] text-foreground-500 leading-relaxed mb-4">
          {group.description}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-2.5 flex-1 content-center">
          {group.items.map((item, ii) => (
            <TechItem
              key={item.name}
              name={item.name}
              logo={item.logo}
              icon={item.icon}
              featured={item.featured}
              href={item.href}
              delay={ii * 60}
            />
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

function FeaturePanel({
  data,
  index,
  reverse,
}: {
  data: { badge: string; title: string; text: string; items: { name: string; logo?: string; icon?: string }[]; cta: string; href: string };
  index: number;
  reverse?: boolean;
}) {
  return (
    <section className={`py-20 md:py-28 ${index % 2 === 0 ? "bg-background-50" : "bg-background-100"}`}>
      <div className="container-astro">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "" : ""}`}>
          <AnimatedSection className={reverse ? "lg:order-2" : ""}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-accent-500/5 to-secondary-500/5 rounded-3xl" />
              <div className="relative p-6 md:p-8 rounded-2xl bg-gradient-to-br from-primary-800 to-primary-900 border border-primary-700/60">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-500/15 text-accent-400 text-xs font-bold uppercase tracking-wider mb-5">
                  {data.badge}
                </span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-background-50 mb-4 font-heading tracking-tight">
                  {data.title}
                </h2>
                <p className="text-sm md:text-base text-background-400 leading-relaxed mb-6">
                  {data.text}
                </p>
                <a
                  href={data.href}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
                >
                  {data.cta}
                  <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={150} className={reverse ? "lg:order-1" : ""}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
              {data.items.map((item, i) => (
                <div
                  key={item.name}
                  className="flex flex-col items-center gap-2.5 p-4 md:p-5 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-md hover:shadow-accent-500/5 hover:-translate-y-0.5 transition-all duration-300"
                >
                  {item.logo ? (
                    <img
                      src={item.logo}
                      alt={item.name}
                      className="h-10 w-10 object-contain transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                  ) : (
                    <span className="w-10 h-10 flex items-center justify-center text-accent-500">
                      <i className={`${item.icon} text-2xl`} />
                    </span>
                  )}
                  <span className="text-xs font-semibold text-foreground-700 text-center leading-tight">
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

export default function TechnologiesPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Technologies" },
        ]}
      />

      <PageHero
        badge={technologiesPageData.hero.badge}
        headline={technologiesPageData.hero.headline}
        subtext={technologiesPageData.hero.subtext}
        primaryCTA={technologiesPageData.hero.primaryCTA}
        secondaryCTA={technologiesPageData.hero.secondaryCTA}
      />

      {/* Support Points */}
      <section className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Technology That Supports Your Business
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {technologiesPageData.supportPoints.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Category Boxes */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                {techStackData.heading}
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {techStackData.subtext}
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6 auto-rows-fr">
            {technologiesPageData.categories.map((group, i) => (
              <CategoryBox key={group.id} group={group} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Laravel Feature */}
      <FeaturePanel data={technologiesPageData.laravelFeature} index={0} />

      {/* AI Feature */}
      <FeaturePanel data={technologiesPageData.aiFeature} index={1} reverse />

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}